import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  KeyRound,
  Shield,
  Users,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Save,
  CheckCircle,
  XCircle,
  Crown,
  User,
  Briefcase,
  Search,
  Settings,
  LayoutDashboard,
  CreditCard,
  Building2,
  Gift,
  TrendingUp,
  Handshake,
  Gamepad2,
  FileText,
  Clock,
  AlertTriangle,
  Plus,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { AdminService } from '@/domain/admin/admin.service'
import { Admin, AdminGroup, ADMIN_GROUP_CONFIG } from '@/domain/admin/admin.types'
import { PermissionService } from '@/domain/permission/permission.service'
import { RoleService, type MenuPermissionConfig, type MenuActions } from '@/domain/role/role.service'
import { RoleMenuHelper } from '@/domain/role/role-menu.helper'
import { MENU_PERMISSIONS } from '@/hooks/use-permission'
import { useAuth } from '@/components/auth'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

// Types
interface Role {
  id: string
  name: string
  description: string
  level: number
  color: string
  icon: React.ReactNode
  permissions: string[]
  userCount: number
  isSystem: boolean
  createdAt: string
}

// Removed unused interfaces Permission and AdminUser

// Role Data - ตรงกับ admin_group ใน database
const defaultRoles: Role[] = [
  {
    id: '0', // admin_group: 0
    name: 'Super Admin',
    description: 'สิทธิ์สูงสุด จัดการได้ทุกอย่าง',
    level: 0,
    color: 'bg-gradient-to-r from-amber-500 to-orange-500',
    icon: <Crown className="h-4 w-4" />,
    permissions: ['all'],
    userCount: 0,
    isSystem: true,
    createdAt: '2025-01-01',
  },
  {
    id: '1', // admin_group: 1
    name: 'Admin',
    description: 'แอดมิน จัดการระบบตามสิทธิ์ที่กำหนด',
    level: 1,
    color: 'bg-gradient-to-r from-purple-500 to-indigo-500',
    icon: <Shield className="h-4 w-4" />,
    permissions: ['dashboard', 'members', 'transactions', 'banks', 'reports', 'games', 'affiliates', 'promotions'],
    userCount: 0,
    isSystem: true,
    createdAt: '2025-01-01',
  },
  {
    id: '3', // admin_group: 3
    name: 'Partner',
    description: 'พาร์ทเนอร์ ดูรายงานและข้อมูลบางส่วน',
    level: 3,
    color: 'bg-gradient-to-r from-blue-500 to-cyan-500',
    icon: <Briefcase className="h-4 w-4" />,
    permissions: ['dashboard', 'reports', 'affiliates'],
    userCount: 0,
    isSystem: true,
    createdAt: '2025-01-01',
  },
]

const modules = [
  { id: 'dashboard', name: 'แดชบอร์ด', icon: <LayoutDashboard className="h-4 w-4" /> },
  { id: 'members', name: 'สมาชิก', icon: <Users className="h-4 w-4" /> },
  { id: 'transactions', name: 'ธุรกรรม', icon: <CreditCard className="h-4 w-4" /> },
  { id: 'banks', name: 'ธนาคาร', icon: <Building2 className="h-4 w-4" /> },
  { id: 'reports', name: 'รายงาน', icon: <TrendingUp className="h-4 w-4" /> },
  { id: 'affiliates', name: 'พันธมิตร', icon: <Handshake className="h-4 w-4" /> },
  { id: 'games', name: 'เกม', icon: <Gamepad2 className="h-4 w-4" /> },
  { id: 'promotions', name: 'โปรโมชั่น', icon: <Gift className="h-4 w-4" /> },
  { id: 'admins', name: 'ผู้ดูแล', icon: <Shield className="h-4 w-4" /> },
  { id: 'audit', name: 'Audit Log', icon: <FileText className="h-4 w-4" /> },
  { id: 'cronjobs', name: 'Cron Jobs', icon: <Clock className="h-4 w-4" /> },
  { id: 'settings', name: 'ตั้งค่า', icon: <Settings className="h-4 w-4" /> },
  { id: 'acl', name: 'จัดการสิทธิ์', icon: <KeyRound className="h-4 w-4" /> },
]

// Removed mockAdmins and defaultPermissions - using real data from AdminService

export function AclPage() {
  const { sessionToken } = useAuth()
  const [activeTab, setActiveTab] = useState('roles')
  const [roles, setRoles] = useState<Role[]>([])
  const [admins, setAdmins] = useState<Admin[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null)
  const [selectedRole, setSelectedRole] = useState<Role | null>(null)
  const [showPermissionDialog, setShowPermissionDialog] = useState(false)
  const [showRolePermissionDialog, setShowRolePermissionDialog] = useState(false)
  const [showRoleDialog, setShowRoleDialog] = useState(false)
  const [selectedRoleGroup, setSelectedRoleGroup] = useState<AdminGroup | null>(null)
  const [isSavingRole, setIsSavingRole] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [menuPermissions, setMenuPermissions] = useState<Record<string, boolean>>({})
  const [rolePermissions, setRolePermissions] = useState<MenuPermissionConfig[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [isSavingRolePermissions, setIsSavingRolePermissions] = useState(false)
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set())

  // Load admins and roles
  useEffect(() => {
    if (sessionToken) {
      loadAdmins()
      loadRoles()
    }
  }, [sessionToken])

  const loadRoles = useCallback(() => {
    // Map defaultRoles to actual roles with permissions from RoleService
    // role.id ตรงกับ admin_group ใน database
    const mappedRoles = defaultRoles.map(role => {
      const adminGroup = Number(role.id) as AdminGroup
      
      // ดึง permissions จาก RoleService
      const permissions = RoleService.getRolePermissions(adminGroup)
      
      // นับจำนวน admin ที่มี admin_group นี้
      const userCount = admins.filter(a => a.admin_group === adminGroup).length
      
      return {
        ...role,
        permissions,
        userCount,
      }
    })
    
    setRoles(mappedRoles)
  }, [admins])

  // Reload roles when admins change
  useEffect(() => {
    loadRoles()
  }, [loadRoles])

  const loadAdmins = async () => {
    setIsLoading(true)
    try {
      const result = await AdminService.findAll({ limit: 100 })
      setAdmins(result.admins)
    } catch (error: any) {
      console.error('Error loading admins:', error)
      toast.error('ไม่สามารถโหลดข้อมูลผู้ดูแลได้')
    } finally {
      setIsLoading(false)
    }
  }

  const filteredAdmins = useMemo(() => {
    if (!searchQuery) return admins
    const q = searchQuery.toLowerCase()
    return admins.filter(a => 
      a.firstname.toLowerCase().includes(q) || 
      a.lastname.toLowerCase().includes(q) ||
      a.username.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      ADMIN_GROUP_CONFIG[a.admin_group]?.labelTh.toLowerCase().includes(q)
    )
  }, [admins, searchQuery])

  const stats = useMemo(() => ({
    totalRoles: roles.length,
    systemRoles: roles.filter(r => r.isSystem).length,
    customRoles: roles.filter(r => !r.isSystem).length,
    totalAdmins: admins.length,
    activeAdmins: admins.filter(a => a.status === 'Active').length,
  }), [roles, admins])

  // Removed getRoleById and getStatusBadge - using Admin data directly

  const handleEditPermissions = async (admin: Admin) => {
    setSelectedAdmin(admin)
    
    try {
      // ดึง permissions ปัจจุบัน
      const currentPermissions = await PermissionService.getAdminPermissions(admin.id)
      
      // แปลงเป็น object สำหรับแสดงใน UI
      const permissionsObj = PermissionService.permissionsToObject(currentPermissions)
      setMenuPermissions(permissionsObj)
      
      setShowPermissionDialog(true)
    } catch (error: any) {
      console.error('Error loading permissions:', error)
      toast.error('ไม่สามารถโหลดสิทธิ์ได้')
    }
  }

  const handleSavePermissions = async () => {
    if (!selectedAdmin) return

    setIsSaving(true)
    try {
      // แปลง object เป็น array
      const permissionsArray = PermissionService.objectToPermissions(menuPermissions)
      
      // บันทึก
      await PermissionService.updateAdminPermissions(selectedAdmin.id, permissionsArray)
      
      toast.success('บันทึกสิทธิ์สำเร็จ')
      setShowPermissionDialog(false)
      
      // Reload admins
      await loadAdmins()
    } catch (error: any) {
      console.error('Error saving permissions:', error)
      toast.error('ไม่สามารถบันทึกสิทธิ์ได้: ' + (error.message || 'เกิดข้อผิดพลาด'))
    } finally {
      setIsSaving(false)
    }
  }

  const toggleMenuPermission = (path: string) => {
    setMenuPermissions(prev => ({
      ...prev,
      [path]: !prev[path],
    }))
  }

  const handleEditRole = (admin: Admin) => {
    setSelectedAdmin(admin)
    setSelectedRoleGroup(admin.admin_group)
    setShowRoleDialog(true)
  }

  const handleSaveRole = async () => {
    if (!selectedAdmin || selectedRoleGroup === null) return

    setIsSavingRole(true)
    try {
      await AdminService.update(selectedAdmin.id, {
        admin_group: selectedRoleGroup,
      })

      toast.success('บันทึกบทบาทสำเร็จ')
      setShowRoleDialog(false)
      
      // Reload admins
      await loadAdmins()
    } catch (error: any) {
      console.error('Error saving role:', error)
      toast.error('ไม่สามารถบันทึกบทบาทได้: ' + (error.message || 'เกิดข้อผิดพลาด'))
    } finally {
      setIsSavingRole(false)
    }
  }

  const handleEditRolePermissions = useCallback(async (role: Role) => {
    if (!sessionToken) {
      toast.error('กรุณาเข้าสู่ระบบก่อน')
      return
    }
    
    setSelectedRole(role)
    setShowRolePermissionDialog(true)
    setIsSavingRolePermissions(false)
    
    // role.id ตรงกับ admin_group ใน database
    const adminGroup = Number(role.id) as AdminGroup
    
    try {
      // ✅ ดึง permissions จาก database
      const currentPermissions = await RoleService.getRolePermissionsWithActions(sessionToken, adminGroup)
      
      // ตรวจสอบว่า menu structure มี path ใหม่ที่ยังไม่มีใน permissions หรือไม่
      const allMenuPaths = RoleMenuHelper.getAllMenuPaths()
      const existingPaths = currentPermissions.map(p => p.path)
      const missingPaths = allMenuPaths.filter(p => !existingPaths.includes(p))
      
      // เพิ่ม missing paths
      const completePermissions = [
        ...currentPermissions,
        ...missingPaths.map(path => ({
          path,
          enabled: false,
          actions: {
            view: false,
            create: false,
            edit: false,
            delete: false,
            export: false,
            approve: false,
          },
        })),
      ]
      
      setRolePermissions(completePermissions)
      
      // Auto-expand all menus with children when opening dialog
      const menusWithChildren = RoleMenuHelper.getMenuStructure()
        .filter(m => m.children && m.children.length > 0)
        .map(m => m.path)
      setExpandedMenus(new Set(menusWithChildren))
    } catch (error) {
      console.error('[AclPage] Error loading role permissions:', error)
      toast.error('เกิดข้อผิดพลาดในการโหลด permissions')
    }
  }, [sessionToken])

  const handleSaveRolePermissions = useCallback(async () => {
    if (!selectedRole || !sessionToken) {
      toast.error('กรุณาเข้าสู่ระบบก่อน')
      return
    }

    setIsSavingRolePermissions(true)
    try {
      // role.id ตรงกับ admin_group ใน database
      const adminGroup = Number(selectedRole.id) as AdminGroup
      
      // ✅ บันทึก permissions ลง database
      await RoleService.updateRolePermissionsWithActions(sessionToken, adminGroup, rolePermissions)
      
      // ✅ Trigger refresh permissions cache ใน usePermission hook
      // ใช้ localStorage event เพื่อ trigger refresh (cross-tab)
      localStorage.setItem('role_permissions_updated', String(adminGroup))
      localStorage.removeItem('role_permissions_updated') // ✅ Remove immediately to allow next update
      
      // ✅ Trigger custom event สำหรับ same-tab refresh
      window.dispatchEvent(new CustomEvent('rolePermissionsUpdated', { 
        detail: { adminGroup } 
      }))
      
      toast.success('บันทึกสิทธิ์บทบาทสำเร็จ')
      setShowRolePermissionDialog(false)
      setSelectedRole(null)
      setRolePermissions([])
      
      // Reload roles (อัพเดท permissions และ userCount)
      loadRoles()
    } catch (error: any) {
      console.error('Error saving role permissions:', error)
      toast.error('ไม่สามารถบันทึกสิทธิ์บทบาทได้: ' + (error.message || 'เกิดข้อผิดพลาด'))
    } finally {
      setIsSavingRolePermissions(false)
    }
  }, [selectedRole, rolePermissions, sessionToken, loadRoles])

  const toggleRolePermission = (path: string) => {
    setRolePermissions(prev => prev.map(p => 
      p.path === path ? { ...p, enabled: !p.enabled } : p
    ))
  }

  const toggleRoleAction = (path: string, action: keyof MenuActions) => {
    setRolePermissions(prev => prev.map(p => 
      p.path === path ? {
        ...p,
        actions: {
          ...p.actions,
          [action]: !p.actions[action],
        },
      } : p
    ))
  }

  const toggleMenuExpand = (path: string) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev)
      if (newSet.has(path)) {
        newSet.delete(path)
      } else {
        newSet.add(path)
      }
      return newSet
    })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Access Control List (ACL)</h1>
          <p className="text-muted-foreground">จัดการบทบาทและสิทธิ์การเข้าถึงระบบ</p>
        </div>
        {/* Removed create role button - focus on admin permissions */}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Shield className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">บทบาททั้งหมด</p>
                <p className="text-2xl font-bold">{stats.totalRoles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500/10">
                <Crown className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">บทบาทระบบ</p>
                <p className="text-2xl font-bold">{stats.systemRoles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <KeyRound className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">บทบาทกำหนดเอง</p>
                <p className="text-2xl font-bold">{stats.customRoles}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ผู้ดูแลทั้งหมด</p>
                <p className="text-2xl font-bold">{stats.totalAdmins}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <CheckCircle className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ใช้งานอยู่</p>
                <p className="text-2xl font-bold text-win">{stats.activeAdmins}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="roles" className="gap-2">
            <Shield className="h-4 w-4" />
            บทบาท (Roles)
          </TabsTrigger>
          <TabsTrigger value="admins" className="gap-2">
            <Users className="h-4 w-4" />
            ผู้ดูแล (Admins)
          </TabsTrigger>
          <TabsTrigger value="matrix" className="gap-2">
            <KeyRound className="h-4 w-4" />
            Permission Matrix
          </TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {roles.map((role) => (
              <Card key={role.id} className="overflow-hidden">
                <div className={cn("h-2", role.color)} />
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("p-2.5 rounded-lg text-white", role.color)}>
                        {role.icon}
                      </div>
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {role.name}
                          {role.isSystem && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">ระบบ</Badge>
                          )}
                        </CardTitle>
                        <CardDescription className="text-xs">{role.description}</CardDescription>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem 
                          className="gap-2"
                          onClick={() => handleEditRolePermissions(role)}
                        >
                          <KeyRound className="h-4 w-4" /> จัดการสิทธิ์
                        </DropdownMenuItem>
                        {!role.isSystem && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="gap-2 text-destructive">
                              <Trash2 className="h-4 w-4" /> ลบ
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">ระดับสิทธิ์</span>
                    <Badge variant="outline">Level {role.level}</Badge>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">ผู้ใช้งาน</span>
                    <span className="font-medium">{role.userCount} คน</span>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">สิทธิ์การเข้าถึง</p>
                    <div className="flex flex-wrap gap-1">
                      {role.permissions.includes('all') ? (
                        <Badge variant="secondary" className="bg-amber-500/10 text-amber-500 text-[10px]">
                          สิทธิ์ทั้งหมด
                        </Badge>
                      ) : (
                        role.permissions.slice(0, 4).map((perm) => (
                          <Badge key={perm} variant="secondary" className="text-[10px]">
                            {modules.find(m => m.id === perm)?.name || perm}
                          </Badge>
                        ))
                      )}
                      {!role.permissions.includes('all') && role.permissions.length > 4 && (
                        <Badge variant="secondary" className="text-[10px]">
                          +{role.permissions.length - 4}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full gap-2"
                    onClick={() => handleEditRolePermissions(role)}
                  >
                    <KeyRound className="h-3 w-3" />
                    จัดการสิทธิ์
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Admins Tab */}
        <TabsContent value="admins" className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="ค้นหาผู้ดูแล..." 
                className="pl-9" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ผู้ดูแล</TableHead>
                  <TableHead>บทบาท</TableHead>
                  <TableHead>สถานะ</TableHead>
                  <TableHead>เข้าสู่ระบบล่าสุด</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : filteredAdmins.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      ไม่พบข้อมูลผู้ดูแล
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAdmins.map((admin) => {
                    const groupConfig = ADMIN_GROUP_CONFIG[admin.admin_group]
                    
                    return (
                      <TableRow key={admin.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                              <User className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium">{admin.firstname} {admin.lastname}</p>
                              <p className="text-xs text-muted-foreground">{admin.username}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={cn("text-xs", groupConfig?.color)}>
                            {groupConfig?.labelTh || 'ไม่ระบุ'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary" 
                            className={cn(
                              "border-0 text-xs",
                              admin.status === 'Active' ? 'bg-win/10 text-win' : 'bg-muted text-muted-foreground'
                            )}
                          >
                            {admin.status === 'Active' ? 'ใช้งาน' : admin.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {admin.lastLogin || '-'}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                className="gap-2"
                                onClick={() => handleEditRole(admin)}
                              >
                                <Edit className="h-4 w-4" /> เปลี่ยนบทบาท
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="gap-2"
                                onClick={() => handleEditPermissions(admin)}
                              >
                                <KeyRound className="h-4 w-4" /> จัดการสิทธิ์
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="gap-2 text-destructive">
                                <XCircle className="h-4 w-4" /> ระงับ
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Permission Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Permission Matrix</CardTitle>
              <CardDescription>ภาพรวมสิทธิ์ของแต่ละบทบาท</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-card">โมดูล</TableHead>
                      {roles.map(role => (
                        <TableHead key={role.id} className="text-center min-w-[100px]">
                          <div className="flex flex-col items-center gap-1">
                            <div className={cn("p-1.5 rounded text-white", role.color)}>
                              {role.icon}
                            </div>
                            <span className="text-xs">{role.name}</span>
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {modules.map(module => (
                      <TableRow key={module.id}>
                        <TableCell className="sticky left-0 bg-card">
                          <div className="flex items-center gap-2">
                            {module.icon}
                            <span>{module.name}</span>
                          </div>
                        </TableCell>
                        {roles.map(role => {
                          const hasAccess = role.permissions.includes('all') || role.permissions.includes(module.id)
                          return (
                            <TableCell key={role.id} className="text-center">
                              {hasAccess ? (
                                <CheckCircle className="h-5 w-5 text-win mx-auto" />
                              ) : (
                                <XCircle className="h-5 w-5 text-muted-foreground/30 mx-auto" />
                              )}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Permission Dialog */}
      <Dialog open={showPermissionDialog} onOpenChange={setShowPermissionDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <KeyRound className="h-5 w-5 text-primary" />
              จัดการสิทธิ์ - {selectedAdmin ? `${selectedAdmin.firstname} ${selectedAdmin.lastname}` : 'ผู้ดูแล'}
            </DialogTitle>
            <DialogDescription>
              {selectedAdmin && (
                <>
                  {selectedAdmin.username} • {ADMIN_GROUP_CONFIG[selectedAdmin.admin_group]?.labelTh}
                  {selectedAdmin.admin_group === AdminGroup.SUPER_ADMIN && (
                    <Badge variant="outline" className="ml-2 text-xs">สิทธิ์ทั้งหมด</Badge>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background">เมนู</TableHead>
                    <TableHead className="text-center">เข้าถึงได้</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(MENU_PERMISSIONS).map(([path]) => {
                    const isEnabled = menuPermissions[path] || false
                    const isSuperAdmin = selectedAdmin?.admin_group === AdminGroup.SUPER_ADMIN
                    
                    // หา label จาก sidebar navItems หรือใช้ path
                    const getMenuLabel = (p: string) => {
                      // แปลง path เป็น label
                      const labels: Record<string, string> = {
                        '/dashboard': 'แดชบอร์ด',
                        '/members': 'สมาชิก',
                        '/transactions': 'ธุรกรรม',
                        '/transactions/smart-cashier': 'Smart Cashier',
                        '/transactions/slips': 'สลิป',
                        '/banks': 'ธนาคาร',
                        '/promotions': 'โปรโมชั่น',
                        '/lossback': 'คืนยอดเสีย',
                        '/lossback/manage': 'จัดการคืนยอดเสีย',
                        '/lossback/records': 'รายการยอดเสีย',
                        '/member-groups': 'กลุ่มสมาชิก',
                        '/games': 'เกม',
                        '/games/list': 'รายการเกม',
                        '/games/providers': 'ผู้ให้บริการ',
                        '/games/categories': 'หมวดหมู่',
                        '/games/settings': 'ตั้งค่าเกม',
                        '/games/report': 'รายงานเกม',
                        '/marketing': 'การตลาด',
                        '/marketing/banners': 'แบนเนอร์',
                        '/marketing/campaigns': 'แคมเปญ',
                        '/marketing/popups': 'ป๊อปอัพ',
                        '/marketing/sms': 'SMS',
                        '/marketing/line': 'LINE',
                        '/marketing/email': 'Email',
                        '/marketing/push': 'Push',
                        '/marketing/landing': 'Landing Page',
                        '/reports': 'รายงาน',
                        '/reports/financial': 'รายงานการเงิน',
                        '/reports/monthly': 'รายงานรายเดือน',
                        '/reports/members': 'รายงานสมาชิก',
                        '/reports/transactions': 'รายงานธุรกรรม',
                        '/reports/banks': 'รายงานธนาคาร',
                        '/affiliates': 'พันธมิตร',
                        '/affiliates/list': 'รายชื่อพันธมิตร',
                        '/affiliates/commissions': 'ค่าคอมมิชชั่น',
                        '/affiliates/links': 'ลิงก์แนะนำ',
                        '/affiliates/payouts': 'การจ่ายเงิน',
                        '/affiliates/report': 'รายงานพันธมิตร',
                        '/cron-jobs': 'Cron Jobs',
                        '/settings': 'ตั้งค่า',
                        '/admins': 'ผู้ดูแล',
                        '/acl': 'จัดการสิทธิ์',
                        '/audit-log': 'Audit Log',
                        '/devices': 'อุปกรณ์',
                        '/changelog': 'Changelog',
                      }
                      return labels[p] || p
                    }
                    
                    return (
                      <TableRow key={path}>
                        <TableCell className="sticky left-0 bg-background">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{getMenuLabel(path)}</span>
                            <span className="text-xs text-muted-foreground">({path})</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox 
                            checked={isEnabled || isSuperAdmin}
                            disabled={isSuperAdmin}
                            onCheckedChange={() => toggleMenuPermission(path)}
                          />
                          {isSuperAdmin && (
                            <span className="ml-2 text-xs text-muted-foreground">(Super Admin)</span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </ScrollArea>

          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center gap-2 mr-auto">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <span className="text-xs text-muted-foreground">การเปลี่ยนแปลงจะมีผลทันที</span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowPermissionDialog(false)}
              disabled={isSaving}
            >
              ยกเลิก
            </Button>
            <Button 
              className="gap-2" 
              onClick={handleSavePermissions}
              disabled={isSaving || selectedAdmin?.admin_group === AdminGroup.SUPER_ADMIN}
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  บันทึก
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Edit className="h-5 w-5 text-primary" />
              เปลี่ยนบทบาท - {selectedAdmin ? `${selectedAdmin.firstname} ${selectedAdmin.lastname}` : 'ผู้ดูแล'}
            </DialogTitle>
            <DialogDescription>
              {selectedAdmin && (
                <>
                  {selectedAdmin.username} • บทบาทปัจจุบัน: {ADMIN_GROUP_CONFIG[selectedAdmin.admin_group]?.labelTh}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">เลือกบทบาท</label>
              <Select
                value={selectedRoleGroup?.toString()}
                onValueChange={(value) => setSelectedRoleGroup(Number(value) as AdminGroup)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="เลือกบทบาท" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(ADMIN_GROUP_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={cn("text-xs", config.color)}>
                            {config.labelTh}
                          </Badge>
                          <span className="text-xs text-muted-foreground">({config.description})</span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {selectedRoleGroup !== null && (
              <div className="rounded-lg border p-3 bg-muted/50">
                <p className="text-xs text-muted-foreground mb-1">คำอธิบาย:</p>
                <p className="text-sm">{ADMIN_GROUP_CONFIG[selectedRoleGroup]?.description}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowRoleDialog(false)}
              disabled={isSavingRole}
            >
              ยกเลิก
            </Button>
            <Button 
              onClick={handleSaveRole}
              disabled={isSavingRole || selectedRoleGroup === null || selectedAdmin?.admin_group === selectedRoleGroup}
            >
              {isSavingRole ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  บันทึก
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Permission Dialog */}
      <Dialog open={showRolePermissionDialog} onOpenChange={setShowRolePermissionDialog}>
        <DialogContent className="max-w-[95vw] h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <KeyRound className="h-5 w-5 text-primary" />
              จัดการสิทธิ์บทบาท - {selectedRole?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedRole && (
                <>
                  {selectedRole.description}
                  {selectedRole.isSystem && (
                    <Badge variant="outline" className="ml-2 text-xs">บทบาทระบบ</Badge>
                  )}
                  <div className="mt-2 text-xs text-muted-foreground">
                    การเปลี่ยนแปลงจะมีผลกับ Admin ทั้งหมดที่มีบทบาทนี้ (ยกเว้น Admin ที่ตั้งค่า permissions เอง)
                  </div>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 -mx-6 px-6" style={{ height: 'calc(90vh - 250px)' }}>
            <div className="overflow-x-auto">
              <Table className="w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-background min-w-[200px]">เมนู</TableHead>
                    <TableHead className="text-center w-24">เข้าถึง</TableHead>
                    <TableHead className="text-center w-20">
                      <div className="flex flex-col items-center gap-1">
                        <Eye className="h-4 w-4" />
                        <span className="text-xs font-medium">ดู</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-20">
                      <div className="flex flex-col items-center gap-1">
                        <Plus className="h-4 w-4" />
                        <span className="text-xs font-medium">สร้าง</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-20">
                      <div className="flex flex-col items-center gap-1">
                        <Edit className="h-4 w-4" />
                        <span className="text-xs font-medium">แก้ไข</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-20">
                      <div className="flex flex-col items-center gap-1">
                        <Trash2 className="h-4 w-4" />
                        <span className="text-xs font-medium">ลบ</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-20">
                      <div className="flex flex-col items-center gap-1">
                        <FileText className="h-4 w-4" />
                        <span className="text-xs font-medium">ส่งออก</span>
                      </div>
                    </TableHead>
                    <TableHead className="text-center w-20">
                      <div className="flex flex-col items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-xs font-medium">อนุมัติ</span>
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {RoleMenuHelper.getMenuStructure().map((menuItem) => {
                    // หา permission config สำหรับ menu นี้
                    const permConfig = rolePermissions.find(p => p.path === menuItem.path) || {
                      path: menuItem.path,
                      enabled: false,
                      actions: {
                        view: false,
                        create: false,
                        edit: false,
                        delete: false,
                        export: false,
                        approve: false,
                      },
                    }
                    
                    // role.id ตรงกับ admin_group ใน database
                    const adminGroup = selectedRole ? Number(selectedRole.id) as AdminGroup : AdminGroup.ADMIN
                    
                    const isSuperAdmin = adminGroup === AdminGroup.SUPER_ADMIN
                    const isEnabled = permConfig.enabled || isSuperAdmin
                    
                    // ตรวจสอบว่าเมนูนี้อยู่ใน MENU_PERMISSIONS หรือไม่ (สำหรับแสดง disabled)
                    const isInPermissions = RoleMenuHelper.isPathInPermissions(menuItem.path)
                    
                    return (
                      <React.Fragment key={menuItem.path}>
                        {/* Parent Menu */}
                        <TableRow className={cn(
                          "bg-muted/30",
                          !isInPermissions && "opacity-60"
                        )}>
                          <TableCell className="sticky left-0 bg-background">
                            <div className="flex items-center gap-2">
                              {menuItem.children && menuItem.children.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 -ml-1"
                                  onClick={() => toggleMenuExpand(menuItem.path)}
                                >
                                  {expandedMenus.has(menuItem.path) ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              {(!menuItem.children || menuItem.children.length === 0) && (
                                <span className="w-6" /> // Spacer for alignment
                              )}
                              <span className="font-medium">{menuItem.labelTh}</span>
                              <span className="text-xs text-muted-foreground">({menuItem.path})</span>
                              {!isInPermissions && (
                                <Badge variant="outline" className="text-[10px]">ไม่แสดงในเมนู</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={isEnabled}
                              disabled={isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRolePermission(menuItem.path)}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={permConfig.actions.view || isSuperAdmin}
                              disabled={!isEnabled || isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRoleAction(menuItem.path, 'view')}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={permConfig.actions.create || isSuperAdmin}
                              disabled={!isEnabled || isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRoleAction(menuItem.path, 'create')}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={permConfig.actions.edit || isSuperAdmin}
                              disabled={!isEnabled || isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRoleAction(menuItem.path, 'edit')}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={permConfig.actions.delete || isSuperAdmin}
                              disabled={!isEnabled || isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRoleAction(menuItem.path, 'delete')}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={permConfig.actions.export || isSuperAdmin}
                              disabled={!isEnabled || isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRoleAction(menuItem.path, 'export')}
                            />
                          </TableCell>
                          <TableCell className="text-center">
                            <Checkbox 
                              checked={permConfig.actions.approve || isSuperAdmin}
                              disabled={!isEnabled || isSuperAdmin || !isInPermissions}
                              onCheckedChange={() => toggleRoleAction(menuItem.path, 'approve')}
                            />
                          </TableCell>
                        </TableRow>
                        
                        {/* Children Menus */}
                        {menuItem.children && expandedMenus.has(menuItem.path) && menuItem.children.map((child) => {
                          const childPermConfig = rolePermissions.find(p => p.path === child.path) || {
                            path: child.path,
                            enabled: false,
                            actions: {
                              view: false,
                              create: false,
                              edit: false,
                              delete: false,
                              export: false,
                              approve: false,
                            },
                          }
                          
                          const childIsEnabled = childPermConfig.enabled || isSuperAdmin
                          const childIsInPermissions = RoleMenuHelper.isPathInPermissions(child.path)
                          
                          return (
                            <TableRow 
                              key={child.path}
                              className={cn(
                                "bg-muted/10",
                                !childIsInPermissions && "opacity-60"
                              )}
                            >
                              <TableCell className="sticky left-0 bg-background pl-8">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-muted-foreground">└─ {child.labelTh}</span>
                                  <span className="text-xs text-muted-foreground">({child.path})</span>
                                  {!childIsInPermissions && (
                                    <Badge variant="outline" className="text-[10px]">ไม่แสดงในเมนู</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childIsEnabled}
                                  disabled={isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRolePermission(child.path)}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childPermConfig.actions.view || isSuperAdmin}
                                  disabled={!childIsEnabled || isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRoleAction(child.path, 'view')}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childPermConfig.actions.create || isSuperAdmin}
                                  disabled={!childIsEnabled || isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRoleAction(child.path, 'create')}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childPermConfig.actions.edit || isSuperAdmin}
                                  disabled={!childIsEnabled || isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRoleAction(child.path, 'edit')}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childPermConfig.actions.delete || isSuperAdmin}
                                  disabled={!childIsEnabled || isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRoleAction(child.path, 'delete')}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childPermConfig.actions.export || isSuperAdmin}
                                  disabled={!childIsEnabled || isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRoleAction(child.path, 'export')}
                                />
                              </TableCell>
                              <TableCell className="text-center">
                                <Checkbox 
                                  checked={childPermConfig.actions.approve || isSuperAdmin}
                                  disabled={!childIsEnabled || isSuperAdmin || !childIsInPermissions}
                                  onCheckedChange={() => toggleRoleAction(child.path, 'approve')}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </ScrollArea>

          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center gap-2 mr-auto">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <span className="text-xs text-muted-foreground">
                การเปลี่ยนแปลงจะมีผลกับ Admin ทั้งหมดที่มีบทบาทนี้
              </span>
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowRolePermissionDialog(false)}
              disabled={isSavingRolePermissions}
            >
              ยกเลิก
            </Button>
            <Button 
              className="gap-2" 
              onClick={handleSaveRolePermissions}
              disabled={isSavingRolePermissions || (selectedRole?.id === '0')}
            >
              {isSavingRolePermissions ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  กำลังบันทึก...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  บันทึก
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}










